/**
 * ChunkUploader.js — Resilient large-file uploader with full failure recovery
 *
 * Features:
 * - Auto chunk vs direct upload based on file size (>50MB = chunk)
 * - Session persistence in localStorage — survives page refresh
 * - Resume interrupted uploads by reconnecting to existing session
 * - Retry missing chunks on finalize error
 * - Parallel chunk upload (4 at a time)
 * - Network error retry with exponential backoff
 * - Full cancel support with server cleanup
 */

const CHUNK_API  = "https://cip.medsphere.net/api";
const CHUNK_SIZE = 5 * 1024 * 1024;   // 5 MB
const PARALLEL   = 4;                  // concurrent chunks
export const LARGE_FILE = 50 * 1024 * 1024; // 50 MB threshold
const SESSION_KEY = "pv_chunk_session"; // localStorage key

// ── localStorage session persistence ─────────────────────────────────────────
export function saveSession(data) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify({...data, ts: Date.now()})); } catch {}
}
export function loadSession() {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY)||"null");
    if(!s) return null;
    // sessions expire after 55 mins (server allows 1hr)
    if(Date.now() - s.ts > 55 * 60 * 1000) { clearSession(); return null; }
    return s;
  } catch { return null; }
}
export function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}

// ── Check if a session is still alive on server ───────────────────────────────
export async function checkSession(sessionId, authCode) {
  try {
    const r = await fetch(`${CHUNK_API}/upload_session/${sessionId}`,
      {headers:{"Auth-Code":authCode}});
    const res = await r.json();
    return res.FINALIZED === false && (res.TOTAL||0) > 0
      ? { alive: true, received: res.RECEIVED||0, total: res.TOTAL||0 }
      : { alive: false };
  } catch { return { alive: false }; }
}

// ── Fetch with retry + exponential backoff ────────────────────────────────────
async function fetchRetry(url, opts, retries=3) {
  for(let i=0; i<=retries; i++) {
    try {
      const r = await fetch(url, opts);
      if(r.ok || r.status < 500) return r;
      throw new Error(`HTTP ${r.status}`);
    } catch(e) {
      if(i === retries) throw e;
      await new Promise(r=>setTimeout(r, 1000 * Math.pow(2, i))); // 1s, 2s, 4s
    }
  }
}

// ── Create upload session ─────────────────────────────────────────────────────
export async function createSession({ file, subprojectid, userid, authCode }) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const fd = new FormData();
  fd.append("userid",       userid);
  fd.append("subprojectid", subprojectid);
  fd.append("filename",     file.name);
  fd.append("filesize",     file.size);
  fd.append("filetype",     file.type||"application/octet-stream");
  fd.append("total_chunks", totalChunks);
  const r   = await fetchRetry(`${CHUNK_API}/upload_session`,
    {method:"POST", headers:{"Auth-Code":authCode}, body:fd});
  const res = await r.json();
  if(res.STATUS!=="1"&&res.STATUS!==1) throw new Error(res.MESSAGE||"Session create failed");
  return { sessionId:res.SESSION_ID, filename:res.FILENAME, totalChunks };
}

// ── Upload one chunk ──────────────────────────────────────────────────────────
async function uploadChunk({ file, index, sessionId, authCode }) {
  const start = index * CHUNK_SIZE;
  const end   = Math.min(start + CHUNK_SIZE, file.size);
  const blob  = file.slice(start, end);
  const fd    = new FormData();
  fd.append("session_id",  sessionId);
  fd.append("chunk_index", index);
  fd.append("chunk",       blob, `chunk_${index}`);
  const r   = await fetchRetry(`${CHUNK_API}/upload_chunk`,
    {method:"POST", headers:{"Auth-Code":authCode}, body:fd});
  return r.json();
}

// ── Upload chunks in parallel batches, skipping already received ──────────────
export async function uploadChunks({ file, sessionId, totalChunks, authCode, onProgress, receivedSoFar=0 }) {
  // build list of chunks that still need uploading
  const toUpload = Array.from({length:totalChunks},(_,i)=>i).filter(i=>i>=receivedSoFar);
  let done = receivedSoFar;
  for(let i=0; i<toUpload.length; i+=PARALLEL) {
    const batch = toUpload.slice(i, i+PARALLEL);
    await Promise.all(batch.map(idx => uploadChunk({file,index:idx,sessionId,authCode})));
    done += batch.length;
    onProgress?.(done, totalChunks);
  }
}

// ── Finalize ──────────────────────────────────────────────────────────────────
export async function finalizeUpload({
  sessionId, userid, documentid, subprojectid, authCode,
  screentime="0", isbookmarked="0", bookmark="",
  attachment="false", notify="0"
}) {
  const fd = new FormData();
  fd.append("session_id",   sessionId);
  fd.append("userid",       userid);
  fd.append("documentid",   documentid);
  fd.append("subprojectid", subprojectid);
  fd.append("screentime",   screentime);
  fd.append("isbookmarked", isbookmarked);
  if(bookmark) fd.append("bookmark", bookmark);
  fd.append("attachment",   attachment);
  fd.append("notify",       notify);
  const r   = await fetchRetry(`${CHUNK_API}/upload_finalize`,
    {method:"POST", headers:{"Auth-Code":authCode}, body:fd});
  const res = await r.json();
  if((res.STATUS==="0"||res.STATUS===0) && Array.isArray(res.MISSING) && res.MISSING.length) {
    return { status:"missing", missing:res.MISSING };
  }
  if(res.STATUS!=="1"&&res.STATUS!==1) throw new Error(res.MESSAGE||"Finalize failed");
  return { status:"ok", message:res.MESSAGE||"" };
}

// ── Cancel session on server ──────────────────────────────────────────────────
export async function cancelSession(sessionId, authCode) {
  if(!sessionId) return;
  clearSession();
  try {
    await fetch(`${CHUNK_API}/upload_session/${sessionId}`,
      {method:"DELETE", headers:{"Auth-Code":authCode}});
  } catch {}
}

// ── Main smartUpload — handles all cases with full resilience ─────────────────
/**
 * @param {Object} opts
 * @param {File}   opts.file
 * @param {string} opts.userid
 * @param {string} opts.subprojectid
 * @param {string} opts.documentid
 * @param {string} opts.authCode
 * @param {string} [opts.screentime]
 * @param {string} [opts.isbookmarked]
 * @param {string} [opts.bookmark]
 * @param {string} [opts.attachment]  "true" if more files follow
 * @param {string} [opts.notify]      "1" for last file only
 * @param {Function} [opts.onProgress] (done, total) => void
 * @param {{current}} [opts.sessionRef] ref to hold sessionId for cancel
 * @param {Function} [opts.onStatus]  (msg) => void — status messages for UI
 */
export async function smartUpload({
  file, userid, subprojectid, documentid, authCode,
  screentime="0", isbookmarked="0", bookmark="",
  attachment="false", notify="0",
  onProgress, sessionRef, onStatus
}) {
  const isLarge = file.size > LARGE_FILE;

  // ── Small file — direct upload_thumbs with retry ──────────────────────────
  if(!isLarge) {
    onStatus?.("Uploading…");
    const fd = new FormData();
    fd.append("userid",       userid);
    fd.append("documentid",   documentid);
    fd.append("attachment",   attachment);
    fd.append("notify",       notify);
    fd.append("files",        file, file.name);
    fd.append("screentime",   screentime);
    fd.append("isbookmarked", isbookmarked);
    if(bookmark) fd.append("bookmark", bookmark);
    onProgress?.(50, 100);
    const r   = await fetchRetry(`${CHUNK_API}/upload_thumbs`,
      {method:"POST", headers:{"Auth-Code":authCode}, body:fd});
    const res = await r.json();
    onProgress?.(100, 100);
    return res.MESSAGE||"";
  }

  // ── Large file — chunk upload ─────────────────────────────────────────────
  let sessionId   = null;
  let totalChunks = null;
  let receivedSoFar = 0;

  // 1. Check if there's a saved session we can resume
  const saved = loadSession();
  if(saved && saved.documentid===String(documentid) && saved.filename===file.name) {
    onStatus?.("Reconnecting to previous upload…");
    const check = await checkSession(saved.sessionId, authCode);
    if(check.alive) {
      sessionId     = saved.sessionId;
      totalChunks   = check.total;
      receivedSoFar = check.received;
      onStatus?.("Picking up where you left off…");
    } else {
      clearSession();
    }
  }

  // 2. Create new session if needed
  if(!sessionId) {
    onStatus?.("Preparing upload…");
    const sess = await createSession({file, subprojectid, userid, authCode});
    sessionId   = sess.sessionId;
    totalChunks = sess.totalChunks;
    receivedSoFar = 0;
    // persist to localStorage for resume
    saveSession({sessionId, documentid:String(documentid), filename:file.name, subprojectid, userid});
  }

  if(sessionRef) sessionRef.current = sessionId;

  // 3. Upload remaining chunks
  onStatus?.("Uploading…");
  try {
    await uploadChunks({
      file, sessionId, totalChunks, authCode, receivedSoFar,
      onProgress: (done, total) => onProgress?.(done, total)
    });
  } catch(e) {
    // network failure mid-upload — session saved, user can retry
    throw new Error("Upload paused due to a connection issue. Your progress is saved — just try again to continue.");
  }

  // 4. Finalize with retry on missing chunks
  onStatus?.("Almost done…");
  let attempts = 0;
  while(attempts < 3) {
    const result = await finalizeUpload({
      sessionId, userid, documentid, subprojectid, authCode,
      screentime, isbookmarked, bookmark, attachment, notify
    });
    if(result.status === "ok") {
      clearSession(); // done — clean up
      if(sessionRef) sessionRef.current = null;
      return result.message;
    }
    if(result.status === "missing" && attempts < 2) {
      onStatus?.("Verifying upload…");
      await Promise.all(result.missing.map(idx=>uploadChunk({file,index:idx,sessionId,authCode})));
      attempts++;
    } else {
      throw new Error("Something went wrong. Please try again.");
    }
  }

  clearSession();
  if(sessionRef) sessionRef.current = null;
  return "";
}